import {
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import type {
  EmailSend,
  EmailSendStatus,
  EmailTemplateVariable,
  SendEmailInput,
} from '@prepzio/shared';
import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';

import {
  useEmailSends,
  useEmailTemplateDetail,
  useEmailTemplates,
  useSendEmail,
} from '../hooks/use-emails';
import { useT, type MessageKey, type TranslateFn } from '../i18n';
import { errorMessage } from '../lib/ui';

const STATUS_OPTIONS: Array<EmailSendStatus | ''> = [
  '',
  'queued',
  'sent',
  'delivered',
  'delivery_delayed',
  'bounced',
  'complained',
  'failed',
  'suppressed',
];

/** Map a send status to a Mantine badge color. */
function statusColor(status: EmailSendStatus): string {
  if (status === 'sent' || status === 'delivered') return 'green';
  if (
    status === 'failed' ||
    status === 'bounced' ||
    status === 'complained' ||
    status === 'suppressed'
  )
    return 'red';
  return 'gray';
}

function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

function variableNeedsValue(v: EmailTemplateVariable): boolean {
  return v.fallbackValue == null;
}

function historyLabel(row: EmailSend, t: TranslateFn): string {
  if (row.templateName) return row.templateName;
  if (row.templateId) return row.templateId;
  return t('email.plainLabel');
}

/** A Resend delivery status, in words. Unknown states print themselves. */
function statusLabel(status: EmailSendStatus, t: TranslateFn): string {
  const key = `email.status.${status}` as MessageKey;
  const translated = t(key);
  return translated === key ? status : translated;
}

export function EmailScreen() {
  const t = useT();
  const formId = useId();

  const [mode, setMode] = useState<'template' | 'plain'>('plain');
  const [to, setTo] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [variables, setVariables] = useState<Record<string, string>>({});

  const [subject, setSubject] = useState('');
  const [text, setText] = useState('');
  const [html, setHtml] = useState('');
  const [showHtml, setShowHtml] = useState(false);

  const [validationErr, setValidationErr] = useState<string | null>(null);
  const [sendOk, setSendOk] = useState<string | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);

  const [statusFilter, setStatusFilter] = useState<EmailSendStatus | ''>('');
  const [toFilter, setToFilter] = useState('');

  const templatesQuery = useEmailTemplates();
  const templates = templatesQuery.data?.items ?? [];

  const detailQuery = useEmailTemplateDetail(templateId, mode === 'template');
  const detail = mode === 'template' && templateId ? (detailQuery.data ?? null) : null;
  const detailBusy = mode === 'template' && !!templateId && detailQuery.isFetching;

  const sendsQuery = useEmailSends({ status: statusFilter, to: toFilter });
  const history = useMemo(
    () => sendsQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [sendsQuery.data],
  );

  const send = useSendEmail();
  const busy = send.isPending;

  // Seed the variable inputs whenever the loaded template detail changes.
  useEffect(() => {
    const tpl = detailQuery.data;
    if (mode !== 'template' || !templateId || !tpl) {
      setVariables({});
      return;
    }
    const initial: Record<string, string> = {};
    for (const v of tpl.variables)
      initial[v.key] = v.fallbackValue != null ? String(v.fallbackValue) : '';
    setVariables(initial);
  }, [detailQuery.data, mode, templateId]);

  const missingRequired = useMemo(() => {
    if (!detail) return [] as string[];
    return detail.variables
      .filter(variableNeedsValue)
      .filter((v) => !(variables[v.key] ?? '').trim())
      .map((v) => v.key);
  }, [detail, variables]);

  function onSend(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setValidationErr(null);
    setSendOk(null);

    let input: SendEmailInput;
    if (mode === 'template') {
      if (!detail) return;
      if (missingRequired.length > 0) {
        setValidationErr(t('email.fillRequired', { keys: missingRequired.join(', ') }));
        return;
      }
      if (
        !window.confirm(
          t('email.confirmTemplate', { name: detail.name, to: to.trim().toLowerCase() }),
        )
      )
        return;
      const vars: SendEmailInput['variables'] = {};
      for (const v of detail.variables) {
        const raw = (variables[v.key] ?? '').trim();
        if (!raw) continue;
        vars[v.key] = v.type === 'number' ? Number(raw) : raw;
        if (v.type === 'number' && Number.isNaN(vars[v.key] as number)) {
          setValidationErr(t('email.mustBeNumber', { key: v.key }));
          return;
        }
      }
      input = { to: to.trim().toLowerCase(), templateId: detail.id, variables: vars };
    } else {
      if (!subject.trim() || !text.trim()) {
        setValidationErr(t('email.subjectAndTextRequired'));
        return;
      }
      if (!window.confirm(t('email.confirmPlain', { to: to.trim().toLowerCase() }))) return;
      input = {
        to: to.trim().toLowerCase(),
        variables: {},
        subject: subject.trim(),
        text,
        ...(showHtml && html.trim() ? { html } : {}),
      };
    }

    send.mutate(
      { input, idempotencyKey },
      {
        onSuccess: (result) => {
          setSendOk(
            t(result.replayed ? 'email.replayed' : 'email.sent', {
              id: result.send.id,
              status: statusLabel(result.send.status, t),
            }),
          );
          setIdempotencyKey(newIdempotencyKey());
        },
      },
    );
  }

  const sendErr = validationErr ?? (send.isError ? errorMessage(send.error) : null);

  return (
    <Stack gap="md">
      <Text c="dimmed" size="sm">
        {t('email.intro')}
      </Text>

      <Card
        withBorder
        padding="lg"
        radius="md"
        component="form"
        onSubmit={onSend}
        aria-labelledby={`${formId}-compose`}
      >
        <Stack gap="md">
          <Group align="center" justify="space-between">
            <Text id={`${formId}-compose`} fw={600}>
              {t('email.compose')}
            </Text>
            <SegmentedControl
              aria-label={t('email.composeMode')}
              data={[
                { label: t('email.plain'), value: 'plain' },
                { label: t('email.template'), value: 'template' },
              ]}
              value={mode}
              onChange={(v) => setMode(v as 'template' | 'plain')}
            />
          </Group>

          <TextInput
            label={t('email.recipient')}
            id={`${formId}-to`}
            type="email"
            required
            autoComplete="email"
            value={to}
            onChange={(e) => setTo(e.currentTarget.value)}
            placeholder="user@example.com"
          />

          {mode === 'template' && (
            <Select
              label={t('email.publishedTemplate')}
              id={`${formId}-template`}
              required
              data={templates.map((tpl) => ({
                value: tpl.id,
                label: `${tpl.name}${tpl.alias ? ` (${tpl.alias})` : ''}`,
              }))}
              value={templateId}
              onChange={(v) => setTemplateId(v ?? '')}
              placeholder={t('email.selectTemplate')}
              searchable
              description={templates.length === 0 ? t('email.noTemplates') : undefined}
            />
          )}

          {mode === 'plain' && (
            <>
              <TextInput
                label={t('email.subject')}
                id={`${formId}-subject`}
                type="text"
                required
                maxLength={200}
                value={subject}
                onChange={(e) => setSubject(e.currentTarget.value)}
                placeholder={t('email.subjectPlaceholder')}
              />
              <Textarea
                label={t('email.message')}
                id={`${formId}-text`}
                required
                autosize
                minRows={7}
                value={text}
                onChange={(e) => setText(e.currentTarget.value)}
                placeholder={t('email.messagePlaceholder')}
              />
              <Checkbox
                label={t('email.appendHtml')}
                checked={showHtml}
                onChange={(e) => setShowHtml(e.currentTarget.checked)}
              />
              {showHtml && (
                <Textarea
                  label={t('email.htmlLabel')}
                  id={`${formId}-html`}
                  autosize
                  minRows={5}
                  value={html}
                  onChange={(e) => setHtml(e.currentTarget.value)}
                  placeholder="<p>Optional HTML…</p>"
                />
              )}
            </>
          )}

          {mode === 'template' && detailBusy && (
            <Text c="dimmed" size="sm" aria-live="polite">
              {t('email.loadingVariables')}
            </Text>
          )}

          {mode === 'template' && detail && detail.variables.length > 0 && (
            <Group>
              {detail.variables.map((v) =>
                v.type === 'number' ? (
                  <NumberInput
                    key={v.key}
                    label={`${v.key}${variableNeedsValue(v) ? ' *' : ''} (${v.type})`}
                    id={`${formId}-var-${v.key}`}
                    required={variableNeedsValue(v)}
                    value={variables[v.key] ?? ''}
                    onChange={(val) =>
                      setVariables((prev) => ({ ...prev, [v.key]: val == null ? '' : String(val) }))
                    }
                    style={{ minWidth: 'min(180px, 100%)', flex: 1 }}
                  />
                ) : (
                  <TextInput
                    key={v.key}
                    label={`${v.key}${variableNeedsValue(v) ? ' *' : ''} (${v.type})`}
                    id={`${formId}-var-${v.key}`}
                    required={variableNeedsValue(v)}
                    value={variables[v.key] ?? ''}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      setVariables((prev) => ({ ...prev, [v.key]: value }));
                    }}
                    style={{ minWidth: 'min(180px, 100%)', flex: 1 }}
                  />
                ),
              )}
            </Group>
          )}

          {mode === 'template' && detail && (
            <Text c="dimmed" size="sm">
              {t('email.templateMeta', {
                subject: detail.subject ?? '—',
                from: detail.from ?? t('email.fromDefault'),
              })}
            </Text>
          )}

          <Group>
            <Button
              type="submit"
              loading={busy}
              disabled={mode === 'template' && (!detail || detailBusy)}
            >
              {t('email.send')}
            </Button>
            <Button
              type="button"
              variant="default"
              onClick={() => setIdempotencyKey(newIdempotencyKey())}
              disabled={busy}
              title={idempotencyKey}
            >
              {t('email.newKey')}
            </Button>
          </Group>

          <div aria-live="polite" aria-atomic="true">
            {sendErr && (
              <Text c="red" size="sm" role="alert">
                {sendErr}
              </Text>
            )}
            {sendOk && (
              <Text c="green" size="sm">
                {sendOk}
              </Text>
            )}
          </div>
        </Stack>
      </Card>

      <Card withBorder padding="lg" radius="md">
        <Stack gap="md">
          <Group align="center" justify="space-between">
            <Text id={`${formId}-history`} fw={600}>
              {t('email.history')}
            </Text>
            <Button
              type="button"
              variant="default"
              onClick={() => void sendsQuery.refetch()}
              disabled={sendsQuery.isFetching}
            >
              {t('common.refresh')}
            </Button>
          </Group>

          <Group align="flex-end">
            <Select
              label={t('common.status')}
              id={`${formId}-status`}
              data={STATUS_OPTIONS.map((s) => ({
                value: s,
                label: s ? statusLabel(s, t) : t('common.all'),
              }))}
              value={statusFilter}
              onChange={(v) => setStatusFilter((v as EmailSendStatus | '') ?? '')}
              allowDeselect={false}
              style={{ minWidth: 'min(160px, 100%)' }}
            />
            <TextInput
              label={t('email.recipient')}
              id={`${formId}-to-filter`}
              type="email"
              value={toFilter}
              onChange={(e) => setToFilter(e.currentTarget.value)}
              placeholder={t('email.filterByEmail')}
              style={{ flex: 1, minWidth: 'min(200px, 100%)' }}
            />
          </Group>

          <div aria-live="polite">
            {sendsQuery.isError && (
              <Text c="red" size="sm" role="alert">
                {errorMessage(sendsQuery.error)}
              </Text>
            )}
            {sendsQuery.isLoading && (
              <Text c="dimmed" size="sm">
                {t('email.loadingHistory')}
              </Text>
            )}
            {!sendsQuery.isLoading && history.length === 0 && !sendsQuery.isError && (
              <Text c="dimmed" size="sm">
                {t('email.noSends')}
              </Text>
            )}
          </div>

          {history.length > 0 && (
            <Stack gap="sm" aria-labelledby={`${formId}-history`}>
              {history.map((row) => (
                <Card key={row.id} withBorder padding="sm" radius="sm">
                  <Group gap="xs" mb={4}>
                    <Badge variant="light" color={statusColor(row.status)}>
                      {statusLabel(row.status, t)}
                    </Badge>
                    <Text fw={600} style={{ wordBreak: 'break-word' }}>
                      {row.toEmail}
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed">
                    {historyLabel(row, t)}
                    {row.subject ? ` · ${row.subject}` : ''}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {row.createdAt}
                  </Text>
                  {row.errorMessage && (
                    <Text c="red" size="xs" style={{ wordBreak: 'break-word' }}>
                      {row.errorCode}: {row.errorMessage}
                    </Text>
                  )}
                  {row.providerMessageId && (
                    <Text size="xs" c="dimmed" style={{ wordBreak: 'break-word' }}>
                      {t('email.resendId', { id: row.providerMessageId })}
                    </Text>
                  )}
                </Card>
              ))}
            </Stack>
          )}

          {sendsQuery.hasNextPage && (
            <Button
              type="button"
              variant="default"
              disabled={sendsQuery.isFetchingNextPage}
              loading={sendsQuery.isFetchingNextPage}
              onClick={() => void sendsQuery.fetchNextPage()}
              style={{ alignSelf: 'flex-start' }}
            >
              {t('email.loadMore')}
            </Button>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
